/**
 * Auth client and session state — task P3-06.
 *
 * Better Auth ships its own typed client, so the SPA uses that rather than a
 * hand-rolled wrapper. The endpoints under `/api/auth/*` are deliberately
 * absent from the OpenAPI document — their shapes are the library's contract,
 * not ours, and a generated client with its own copy of them would be one more
 * thing to keep in step across upgrades.
 *
 * `baseURL` is left empty: the SPA and the API share one origin through Caddy,
 * which is why session cookies work with `SameSite=Lax` and why there is no
 * CORS configuration anywhere in this repository.
 */
import { createAuthClient } from "better-auth/react";
import { twoFactorClient } from "better-auth/client/plugins";

export const authClient = createAuthClient({
    basePath: "/api/auth",
    plugins: [twoFactorClient()],
});

export const { useSession, signIn, signUp, signOut } = authClient;

/**
 * Turns whatever Better Auth threw or returned into one line a person can act
 * on.
 *
 * The library reports failures as `{ error }` on the resolved value rather than
 * by rejecting, which is easy to overlook — a form that only catches rejections
 * silently succeeds on a wrong password.
 */
export function authErrorMessage(result: unknown): string | null {
    const error = (result as { error?: { message?: string; status?: number } })?.error;
    if (!error) return null;

    if (error.status === 429) {
        return "Too many attempts. Wait a few minutes and try again.";
    }
    if (error.status === 403) {
        return "Check your email for a verification link before signing in.";
    }
    return error.message ?? "Something went wrong. Try again.";
}
